import { MySection } from '@/components/custom/my-section';
import { type ProductFormValues, type ProductUpdateValues } from '@/types/product-type';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { Control, Controller } from 'react-hook-form';

interface ProductAttributesFormSectionProps {
  control: Control<ProductFormValues | ProductUpdateValues>;
}

const colorOptions = [
  { label: 'Black / ពណ៌ខ្មៅ', value: 'Black', colorCode: '#000000' },
  { label: 'White / ពណ៌ស', value: 'White', colorCode: '#FFFFFF' },
  { label: 'Gray / ពណ៌ប្រផេះ', value: 'Gray', colorCode: '#808080' },
  { label: 'Red / ពណ៌ក្រហម', value: 'Red', colorCode: '#FF0000' },
  { label: 'Green / ពណ៌បៃតង', value: 'Green', colorCode: '#008000' },
  { label: 'Blue / ពណ៌ខៀវ', value: 'Blue', colorCode: '#0000FF' },
  { label: 'Yellow / ពណ៌លឿង', value: 'Yellow', colorCode: '#FFFF00' },
  { label: 'Orange / ពណ៌ទឹកក្រូច', value: 'Orange', colorCode: '#FFA500' },
  { label: 'Purple / ពណ៌ស្វាយ', value: 'Purple', colorCode: '#800080' },
  { label: 'Pink / ពណ៌ផ្កាឈូក', value: 'Pink', colorCode: '#FFC0CB' },
  { label: 'Brown / ពណ៌ត្នោត', value: 'Brown', colorCode: '#A52A2A' },
  { label: 'Navy / ពណ៌ខៀវចាស់', value: 'Navy', colorCode: '#000080' },
  { label: 'Beige / ពណ៌បៃតងស្រាលត្នោត', value: 'Beige', colorCode: '#F5F5DC' },
  { label: 'Khaki / ពណ៌កាគី', value: 'Khaki', colorCode: '#C3B091' },
  { label: 'Maroon / ពណ៌ក្រហមចាស់', value: 'Maroon', colorCode: '#800000' },
];

const sizeOptions = [
  { label: 'XXS', value: 'XXS' },
  { label: 'XS', value: 'XS' },
  { label: 'S', value: 'S' },
  { label: 'M', value: 'M' },
  { label: 'L', value: 'L' },
  { label: 'XL', value: 'XL' },
  { label: 'XXL', value: 'XXL' },
  { label: 'XXXL', value: 'XXXL' },
];

function renderMultiSelectValues(
  selectedIds: string[],
  options: { label: string; value: string; colorCode?: string }[],
  isColor: boolean = false,
) {
  const selectedItems = options.filter((item) => selectedIds.includes(item.value));

  if (!selectedItems.length) {
    return '';
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {selectedItems.map((item) => (
        <Chip
          key={item.value}
          size="small"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {isColor && item.colorCode && (
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: item.colorCode,
                    border: '1px solid',
                    borderColor: item.colorCode === '#FFFFFF' ? 'grey.300' : 'transparent',
                    flexShrink: 0,
                  }}
                />
              )}
              {item.label}
            </Box>
          }
        />
      ))}
    </Box>
  );
}

export function ProductAttributesFormSection({ control }: ProductAttributesFormSectionProps) {
  return (
    <MySection
      title="Attributes"
      description="Select product attributes (Color and Size) that will be used for variants"
    >
      <Grid container gap={2}>
        <Grid item xs={12}>
          <Controller
            name="colorValues"
            control={control}
            defaultValue={[]}
            render={({ field, fieldState: { error } }) => {
              const fieldValue = Array.isArray(field.value) ? field.value : [];
              const hasValue = fieldValue.length > 0;
              return (
                <FormControl error={!!error} fullWidth>
                  <InputLabel id="color-values-label" shrink={hasValue}>
                    Color
                  </InputLabel>
                  <Select
                    multiple
                    id="color-values-select"
                    labelId="color-values-label"
                    value={fieldValue}
                    onChange={(e) => field.onChange(e.target.value)}
                    onBlur={field.onBlur}
                    name={field.name}
                    renderValue={(selected) =>
                      renderMultiSelectValues(
                        Array.isArray(selected) ? selected : [],
                        colorOptions,
                        true,
                      )
                    }
                  >
                    {colorOptions.map((option) => {
                      const selected = fieldValue.includes(option.value);
                      return (
                        <MenuItem key={option.value} value={option.value}>
                          <Checkbox size="small" disableRipple checked={selected} />
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              flex: 1,
                            }}
                          >
                            {option.colorCode && (
                              <Box
                                sx={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: '50%',
                                  backgroundColor: option.colorCode,
                                  border: '1px solid',
                                  borderColor:
                                    option.colorCode === '#FFFFFF' ? 'grey.300' : 'transparent',
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            {option.label}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {error && <FormHelperText>{error.message}</FormHelperText>}
                </FormControl>
              );
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="sizeValues"
            control={control}
            defaultValue={[]}
            render={({ field, fieldState: { error } }) => {
              const fieldValue = Array.isArray(field.value) ? field.value : [];
              const hasValue = fieldValue.length > 0;
              return (
                <FormControl error={!!error} fullWidth>
                  <InputLabel id="size-values-label" shrink={hasValue}>
                    Size
                  </InputLabel>
                  <Select
                    multiple
                    id="size-values-select"
                    labelId="size-values-label"
                    value={fieldValue}
                    onChange={(e) => field.onChange(e.target.value)}
                    onBlur={field.onBlur}
                    name={field.name}
                    renderValue={(selected) =>
                      renderMultiSelectValues(Array.isArray(selected) ? selected : [], sizeOptions)
                    }
                  >
                    {sizeOptions.map((option) => {
                      const selected = fieldValue.includes(option.value);
                      return (
                        <MenuItem key={option.value} value={option.value}>
                          <Checkbox size="small" disableRipple checked={selected} />
                          {option.label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {error && <FormHelperText>{error.message}</FormHelperText>}
                </FormControl>
              );
            }}
          />
        </Grid>
      </Grid>
    </MySection>
  );
}
